import { EventType, ICreateRoomStateEvent, MatrixClient, MatrixEvent, RoomStateEvent } from 'matrix-js-sdk/src/matrix';
import uuid4 from 'uuid4';

import { RoleAssignment, WorkspacePowerLevels, WorkspaceRoleV2 } from 'modules/CreateWorkspaceModal/types';
import { DefaultRoleTypesEnum } from 'modules/Workspaces/hooks/useCreateWorkspaceRole/utils';
import { CustomMeedioMatrixEvents, buildCanonicalParentEvent, getViaServers } from 'utils/matrixUtils';
import { workspacesConfig, PermissionId } from 'utils/powerLevelsConfig';
import { WORKSPACE_ROLES_EVENT_VERSION } from 'utils/workspaceRolesEvent';

import { WorkspaceType } from '../types';
import { WORKSPACE_META_VERSION } from '../utils';

export const DEFAULT_OWNER_ROLE_POWER_LEVEL = 99;
export const DEFAULT_MEMBER_ROLE_POWER_LEVEL = 0;
export const DEFAULT_BOT_POWER_LEVEL = 100;

const buildScopedPowerLevels = (level: number): WorkspacePowerLevels => ({
  workspace: level,
  chat: level,
  meeting: level,
});

const buildDefaultV2Roles = (ownerRoleId: string, memberRoleId: string): WorkspaceRoleV2[] => [
  {
    id: ownerRoleId,
    name: DefaultRoleTypesEnum.OWNER,
    defaultRoleType: DefaultRoleTypesEnum.OWNER,
    powerLevels: buildScopedPowerLevels(DEFAULT_OWNER_ROLE_POWER_LEVEL),
  },
  {
    id: memberRoleId,
    name: DefaultRoleTypesEnum.MEMBER,
    defaultRoleType: DefaultRoleTypesEnum.MEMBER,
    powerLevels: buildScopedPowerLevels(DEFAULT_MEMBER_ROLE_POWER_LEVEL),
  },
];

export const buildWorkspaceInitialStates = (
  name: string,
  matrixClient: MatrixClient,
  parentWorkspaceId?: string | null,
  alias?: string
): ICreateRoomStateEvent[] => {
  const currentUserId = matrixClient.getUserId();
  if (!currentUserId) throw new Error('Cannot build workspace initial states: matrix client is not signed in');

  const kind = parentWorkspaceId ? WorkspaceType.SUB_WORKSPACE : WorkspaceType.WORKSPACE;

  const ownerRoleId = uuid4();
  const memberRoleId = uuid4();
  const roles = buildDefaultV2Roles(ownerRoleId, memberRoleId);
  const ownerAssignment: RoleAssignment = {
    matrixId: currentUserId,
    roleId: ownerRoleId,
    assignedBy: currentUserId,
  };

  const initialStates: ICreateRoomStateEvent[] = [
    {
      type: EventType.RoomName,
      state_key: '',
      content: { name },
    },
    {
      type: CustomMeedioMatrixEvents.SPACE_META,
      state_key: '',
      content: {
        kind,
        slug: alias,
        version: WORKSPACE_META_VERSION,
      },
    },
    {
      type: CustomMeedioMatrixEvents.WORKSPACE_ROLES,
      content: {
        version: WORKSPACE_ROLES_EVENT_VERSION,
        roles,
        assignments: [ownerAssignment],
      },
    },
  ];

  if (parentWorkspaceId) {
    const viaServers = getViaServers(parentWorkspaceId, matrixClient);

    initialStates.push(buildCanonicalParentEvent(parentWorkspaceId, viaServers));
  }

  return initialStates;
};

// V1 → V2 PermissionId mapping (Wave 2 T-009a repair surface):
// - WorkspacesRolesCreateRole / WorkspacesMembersAdjustRole / WorkspacesRolesAssignRoles /
//   WorkspacesRolesAdjustPermissions → WorkspaceManageRoles (workspace PL 60)
// - WorkspacesGeneralEditLogo / WorkspacesGeneralEditName / WorkspacesGeneralEditUrl → WorkspaceEditSettings (PL 70)
// - WorkspacesGeneralDelete → WorkspaceDelete (PL 98)
// - RoomsCreate → WorkspaceCreateChat (PL 5) — the most permissive workspace room-create
// - WorkspacesMembersInvite → WorkspaceInviteMembers (PL 30)
// - WorkspacesMembersRemove → WorkspaceRemoveMembers (PL 40)
// - WorkspacesRolesBan → WorkspaceBanMembers (PL 50)
// - WorkspacesRedactEvents → no V2 equivalent (Decision 14 — RedactEvents dropped); preserve
//   V1 numeric threshold of 50 inline. V2 spec §3.7 moves m.room.power_levels initial-state
//   shape out of this file in Wave 4; this is interim repair to keep tsc clean.
const REDACT_EVENTS_POWER_LEVEL = 50;
const roleManagementPowerLevel = workspacesConfig[PermissionId.WorkspaceManageRoles].powerLevel;

const eventPowerLevels: Record<string, number> = {
  [EventType.RoomAvatar]: workspacesConfig[PermissionId.WorkspaceEditSettings].powerLevel,
  [EventType.RoomName]: workspacesConfig[PermissionId.WorkspaceEditSettings].powerLevel,
  [EventType.RoomCanonicalAlias]: workspacesConfig[PermissionId.WorkspaceEditSettings].powerLevel,
  [EventType.RoomTombstone]: workspacesConfig[PermissionId.WorkspaceDelete].powerLevel,
  [EventType.RoomCreate]: workspacesConfig[PermissionId.WorkspaceCreateChat].powerLevel,
  [EventType.RoomJoinRules]: workspacesConfig[PermissionId.WorkspaceDelete].powerLevel,
  [EventType.SpaceChild]: workspacesConfig[PermissionId.WorkspaceCreateChat].powerLevel,
  [EventType.RoomPowerLevels]: roleManagementPowerLevel,
  [CustomMeedioMatrixEvents.WORKSPACE_ROLES]: roleManagementPowerLevel,
  [CustomMeedioMatrixEvents.WORKSPACE_DELETE]: workspacesConfig[PermissionId.WorkspaceDelete].powerLevel,
};

export const workspacePowerLevelOverrides = {
  stateDefault: 20,
  invite: workspacesConfig[PermissionId.WorkspaceInviteMembers].powerLevel,
  kick: workspacesConfig[PermissionId.WorkspaceRemoveMembers].powerLevel,
  ban: workspacesConfig[PermissionId.WorkspaceBanMembers].powerLevel,
  redact: REDACT_EVENTS_POWER_LEVEL,
  events: eventPowerLevels,
};

export const waitForRoleSync = (matrixClient: MatrixClient, roomId: string) =>
  new Promise((resolve) => {
    const resolveAndRemoveListener = () => {
      if (timeout) clearTimeout(timeout);
      matrixClient.off(RoomStateEvent.Events, resolveOnRoleEvent);
      resolve(true);
    };

    const timeout = setTimeout(resolveAndRemoveListener, 1000);

    const resolveOnRoleEvent = (event: MatrixEvent) => {
      const isCurrentRoomEvent = event.getRoomId() === roomId;
      const isRoleEvent = event.getType() === CustomMeedioMatrixEvents.WORKSPACE_ROLES;

      if (isCurrentRoomEvent && isRoleEvent) resolveAndRemoveListener();
    };

    matrixClient.on(RoomStateEvent.Events, resolveOnRoleEvent);
  });
